/**
 * Graphics topic manager.
 *
 * Handles messages on the "graphics" namespace. The backend (see
 * graphic_service.py) publishes two past-tense event types:
 *   - "updated" -> upsert the graphic (emitted for BOTH creates and patches)
 *   - "deleted" -> remove the graphic from the store
 *
 * There is no "moved": a graphic's geometry *is* the entity, so moving it is an
 * ordinary update.
 *
 * Incoming payloads are CoT protobuf binaries decoded via cotUtils. A payload
 * without a SIDC or without parseable geometry is dropped rather than partially
 * applied — an incomplete shape is invalid, not degraded.
 */

import { TopicManager } from "../types";
import { AppDispatch } from "../../../store";
import { graphicsSlice } from "../../../store/graphicsSlice";
import { parseGraphicCot, parseCotMessage } from "../../../utils/cotUtils";

export class GraphicsManager implements TopicManager {
    constructor(private dispatch: AppDispatch) {}

    handleMessage(eventType: string, payload: Buffer): void {
        const bytes = new Uint8Array(payload);

        if (eventType === "deleted") {
            /*
             * A tombstone carries only identity — no geometry — so it cannot go
             * through parseGraphicCot, which requires a renderable shape.
             */
            const parsed = parseCotMessage(bytes);
            if (parsed?.uid) {
                this.dispatch(graphicsSlice.actions.removeGraphic(parsed.uid));
            }
            return;
        }

        if (eventType !== "updated") return;

        const graphic = parseGraphicCot(bytes);
        if (!graphic) return;

        this.dispatch(
            graphicsSlice.actions.upsertGraphic({
                id: graphic.id,
                sidc: graphic.sidc,
                points: graphic.points,
                params: graphic.params,
                faction_id: graphic.factionId,
                unit_id: graphic.unitId,
                marker_id: graphic.markerId,
                name: graphic.name,
            }),
        );
    }
}
